/**
 * Orval Custom Instance
 *
 * Orval에서 생성된 훅들이 사용할 커스텀 axios 인스턴스입니다.
 * camelCase ↔ snake_case 자동 변환 및 인증 처리를 포함합니다.
 *
 * @see https://orval.dev/guides/custom-axios
 */

import Axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from '@/lib/auth/cookies';

// ============================================================================
// Case Conversion Utilities
// ============================================================================

type ConvertibleValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | ConvertibleObject
  | ConvertibleArray;

interface ConvertibleObject {
  [key: string]: ConvertibleValue;
}

type ConvertibleArray = ConvertibleValue[];

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

function camelToSnake(str: string): string {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase();
}

function convertKeysToCamel(obj: ConvertibleValue): ConvertibleValue {
  if (Array.isArray(obj)) {
    return obj.map(convertKeysToCamel);
  } else if (obj !== null && typeof obj === 'object') {
    const objRecord = obj as ConvertibleObject;
    return Object.keys(objRecord).reduce((result: ConvertibleObject, key) => {
      result[snakeToCamel(key)] = convertKeysToCamel(objRecord[key]);
      return result;
    }, {}) as ConvertibleValue;
  }
  return obj;
}

function convertKeysToSnake(obj: ConvertibleValue): ConvertibleValue {
  if (Array.isArray(obj)) {
    return obj.map(convertKeysToSnake);
  } else if (obj !== null && typeof obj === 'object') {
    const objRecord = obj as ConvertibleObject;
    return Object.keys(objRecord).reduce((result: ConvertibleObject, key) => {
      result[camelToSnake(key)] = convertKeysToSnake(objRecord[key]);
      return result;
    }, {}) as ConvertibleValue;
  }
  return obj;
}

// ============================================================================
// Axios Instance
// ============================================================================

// baseURL에서 /api 접미사 제거 (Orval 생성 경로에 이미 /api가 포함됨)
const rawBaseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
const API_BASE_URL = rawBaseUrl.replace(/\/api\/?$/, '');

export const AXIOS_INSTANCE = Axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor: camelCase → snake_case 변환 + 인증 헤더
AXIOS_INSTANCE.interceptors.request.use((config) => {
  // 인증 토큰 추가
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Request body를 snake_case로 변환 (FormData는 제외 - 파일 업로드 깨짐 방지)
  if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
    config.data = convertKeysToSnake(config.data);
  }

  // Query params를 snake_case로 변환
  if (config.params && typeof config.params === 'object') {
    config.params = convertKeysToSnake(config.params);
  }

  return config;
});

// 동시 401 dedup: 여러 요청이 동시에 만료를 감지해도 refresh는 한 번만 수행
let refreshInFlight: Promise<string | null> | null = null;

/**
 * Refresh token으로 새 access token 발급.
 * 인터셉터 재귀를 피하기 위해 raw Axios로 호출 (AXIOS_INSTANCE 사용 X).
 * SimpleJWT는 ROTATE_REFRESH_TOKENS=True 설정이라 새 access+refresh가 모두 반환됨.
 */
export async function performTokenRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await Axios.post<{ access: string; refresh: string }>(
      `${API_BASE_URL}/api/auth/refresh/`,
      { refresh: refreshToken },
      {
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000,
      }
    );
    const { access, refresh } = response.data;
    setTokens(access, refresh);
    return access;
  } catch {
    return null;
  }
}

function isAuthRetryableUrl(url: string | undefined): boolean {
  if (!url) {
    return false;
  }
  // 인증 관련 엔드포인트는 401이어도 refresh로 재시도하지 않음 (무한루프 방지)
  return (
    url.includes('/api/auth/refresh/') ||
    url.includes('/api/auth/login/') ||
    url.includes('/api/auth/register/')
  );
}

// Response Interceptor: snake_case → camelCase 변환 + access token 자동 갱신
AXIOS_INSTANCE.interceptors.response.use(
  (response) => {
    // Response data를 camelCase로 변환
    if (response.data && typeof response.data === 'object') {
      response.data = convertKeysToCamel(response.data);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (AxiosRequestConfig & { _retry?: boolean })
      | undefined;

    // 401 → refresh 후 원 요청 replay 시도
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthRetryableUrl(originalRequest.url)
    ) {
      originalRequest._retry = true;

      if (!refreshInFlight) {
        refreshInFlight = performTokenRefresh().finally(() => {
          refreshInFlight = null;
        });
      }
      const newAccessToken = await refreshInFlight;

      if (newAccessToken) {
        // 원 요청을 새 토큰으로 replay
        // (request 인터셉터의 snake_case/camel_case 변환은 idempotent하므로 이중 변환 안전)
        originalRequest.headers = originalRequest.headers || {};
        (originalRequest.headers as Record<string, string>).Authorization =
          `Bearer ${newAccessToken}`;
        return AXIOS_INSTANCE(originalRequest);
      }
      // refresh 실패 → 아래 401 처리로 fall-through
    }

    // 401 에러 시 토큰 삭제 및 로그인 페이지로 이동
    // 단, 이미 로그인/회원가입 페이지면 무한 새로고침 방지 (Tauri static export 환경)
    if (error.response?.status === 401) {
      clearTokens();
      if (typeof window !== 'undefined') {
        const path = window.location.pathname;
        const onAuthPage = path.startsWith('/login') || path.startsWith('/register');
        if (!onAuthPage) {
          window.location.href = '/login/';  // trailingSlash 필수 (Next.js export)
        }
      }
    }

    // 402 에러 — 크레딧 부족: 전역 모달 표시
    if (error.response?.status === 402) {
      const data = error.response.data as Record<string, unknown>;
      const required = (data?.required as number) ?? 0;
      const balance = (data?.balance as number) ?? 0;
      // 동적 import로 circular dependency 방지
      import('@/lib/stores/uiStore').then(({ useUIStore }) => {
        useUIStore.getState().showInsufficientCredits(required, balance);
      });
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// Custom Instance for Orval
// ============================================================================

/**
 * Orval mutator로 사용되는 커스텀 인스턴스
 *
 * @example
 * // orval.config.ts에서 설정
 * mutator: {
 *   path: './src/lib/api/orval/custom-instance.ts',
 *   name: 'customInstance',
 * }
 */
export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  const source = Axios.CancelToken.source();

  // FormData인 경우 Content-Type을 설정하지 않음 (브라우저가 boundary 포함하여 자동 설정)
  const isFormData = config.data instanceof FormData;

  const mergedConfig = {
    ...config,
    ...options,
    headers: {
      ...config.headers,
      ...options?.headers,
      // FormData가 아닌 경우에만 JSON으로 설정
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    },
    cancelToken: source.token,
  };

  const promise = AXIOS_INSTANCE(mergedConfig).then(({ data }) => data);

  // @ts-expect-error - cancel 메서드 추가 (React Query에서 사용)
  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise;
};

// ============================================================================
// Error Type Export (Orval에서 사용)
// ============================================================================

export type ErrorType<Error> = AxiosError<Error>;

export type BodyType<BodyData> = BodyData;
